import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import VisualizerBackground from '../components/VisualizerBackground';
import Metadata from '../components/Metadata';
import { Star, MessageSquare, User, Award } from 'lucide-react';
import { venuePhotos } from '../data/photos';
import testimonialData from '../data/testimonials.json';

// Define the Testimonial type
interface TestimonialType {
  id: number;
  name: string;
  role: string;
  rating: number;
  text: string;
  service: string;
  featured: boolean;
  approved?: boolean;
  flagged?: boolean;
  date?: string;
}

const Testimonials = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    service: '',
    rating: 5,
    testimonial: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating: number) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // With Netlify Forms, the form will be handled automatically
    const form = e.target as HTMLFormElement;
    
    // Simulate a delay to show the loading state
    setTimeout(() => {
      form.submit();
    }, 500);
  };

  // Filter testimonials to only show approved ones
  const approvedTestimonials = (testimonialData.testimonials as TestimonialType[])
    .filter(t => t.approved !== false && t.flagged !== true);
  
  const featuredTestimonials = approvedTestimonials.filter(t => t.featured);
  const regularTestimonials = approvedTestimonials.filter(t => !t.featured);

  return (
    <div className="min-h-screen bg-drummer-dark text-white">
      <Metadata 
        title="Testimonials & Feedback | Daryian Kelton"
        description="Read testimonials from clients who have worked with Daryian Kelton and share your own experience."
        pageType="home"
      />
      <VisualizerBackground />
      
      {/* Hero Section */}
      <section className="pt-24 pb-8 md:pt-32 md:pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">Client Testimonials</span>
            </h1>
            <p className="text-xl text-white">
              Hear what others have to say about their experience working with me
            </p>
          </div>
        </div>
      </section>
      
      {/* Featured Testimonials */}
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4">
          <h2 className="section-heading text-center mb-12">Featured Feedback</h2>
          
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {featuredTestimonials.map(testimonial => (
              <div key={testimonial.id} className="glassmorphic p-6 relative">
                <div className="absolute top-6 right-6 flex">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} fill="#FFBA00" color="#FFBA00" size={18} />
                  ))}
                </div>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-client-yellow to-client-teal rounded-full flex items-center justify-center mr-4">
                    <User className="text-black" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{testimonial.name}</h3>
                    <p className="text-sm text-white">{testimonial.role}</p>
                  </div>
                </div>
                <p className="mb-4 text-white">{testimonial.text}</p>
                <div className="flex items-center">
                  <Award size={16} className="text-client-yellow mr-2" />
                  <span className="text-sm text-client-yellow">{testimonial.service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* More Testimonials */}
      <section className="py-8 md:py-12 bg-black/30">
        <div className="container mx-auto px-4">
          <h2 className="section-heading text-center mb-12">More Client Stories</h2>
          
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {regularTestimonials.map(testimonial => (
              <div key={testimonial.id} className="glassmorphic p-6 relative">
                <div className="absolute top-6 right-6 flex">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} fill="#FFBA00" color="#FFBA00" size={18} />
                  ))}
                </div>
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-client-yellow to-client-teal rounded-full flex items-center justify-center mr-3">
                    <User className="text-black" size={16} />
                  </div>
                  <div>
                    <h3 className="font-bold">{testimonial.name}</h3>
                    <p className="text-xs text-white">{testimonial.role}</p>
                  </div>
                </div>
                <p className="mb-4 text-sm text-white">{testimonial.text}</p>
                <div className="flex items-center">
                  <Award size={14} className="text-client-yellow mr-2" />
                  <span className="text-xs text-client-yellow">{testimonial.service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Submit Feedback Form */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="section-heading text-center mb-12">Share Your Experience</h2>
            
            <div className="glassmorphic p-8">
              <form 
                onSubmit={handleSubmit} 
                className="space-y-6"
                name="testimonial"
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
                action="/success?form=testimonial"
              >
                <input type="hidden" name="form-name" value="testimonial" />
                <div className="hidden">
                  <label>
                    Don't fill this out if you're human: <input name="bot-field" />
                  </label>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-white mb-1">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 rounded-md bg-client-black/30 border border-client-gray/20 focus:border-client-yellow focus:ring-1 focus:ring-client-yellow focus:outline-none text-white"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-white mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 rounded-md bg-client-black/30 border border-client-gray/20 focus:border-client-yellow focus:ring-1 focus:ring-client-yellow focus:outline-none text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-white mb-1">
                      Your Role/Company
                    </label>
                    <input
                      type="text"
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      placeholder="e.g. Musician, Band Manager, Studio Owner"
                      className="w-full px-4 py-2 rounded-md bg-client-black/30 border border-client-gray/20 focus:border-client-yellow focus:ring-1 focus:ring-client-yellow focus:outline-none text-white"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-white mb-1">
                      Service Used *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 rounded-md bg-client-black/30 border border-client-gray/20 focus:border-client-yellow focus:ring-1 focus:ring-client-yellow focus:outline-none text-white"
                    >
                      <option value="">Select a service</option>
                      <option value="Session Drummer">Session Drummer</option>
                      <option value="Music Production">Music Production</option>
                      <option value="Content Creation">Content Creation</option>
                      <option value="Drum Repair & Tuning">Drum Repair & Tuning</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Your Rating *
                  </label>
                  <div className="flex space-x-2">
                    {[1, 2, 3, 4, 5].map(rating => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => handleRatingChange(rating)}
                        className="p-2 focus:outline-none focus:ring-2 focus:ring-client-yellow rounded-md"
                        title={`Rate ${rating} stars`}
                        aria-label={`Rate ${rating} stars`}
                      >
                        <Star 
                          size={24} 
                          fill={formData.rating >= rating ? '#FFBA00' : 'transparent'} 
                          color={formData.rating >= rating ? '#FFBA00' : '#46444D'} 
                        />
                      </button>
                    ))}
                    <input 
                      type="hidden" 
                      name="rating" 
                      value={formData.rating} 
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="testimonial" className="block text-sm font-medium text-white mb-1">
                    Your Testimonial *
                  </label>
                  <textarea
                    id="testimonial"
                    name="testimonial"
                    rows={5}
                    value={formData.testimonial}
                    onChange={handleChange}
                    required
                    placeholder="Please share your experience working with me..."
                    className="w-full px-4 py-2 rounded-md bg-client-black/30 border border-client-gray/20 focus:border-client-yellow focus:ring-1 focus:ring-client-yellow focus:outline-none text-white"
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="drum-button w-full flex justify-center items-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="mr-2" size={20} />
                      Submit Feedback
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      
      {/* Call to Action */}
      <section className="py-12 md:py-16 bg-black/30">
        <div className="container mx-auto px-4">
          <div className="glassmorphic p-8 md:p-12">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Share Your Experience?</h2>
              <p className="text-xl text-white mb-6">
                Have you worked with Daryian before? Share your feedback or explore his services to start your own project.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/about"
                  className="drum-button"
                >
                  Learn About Daryian
                </Link>
                <Link 
                  to="/services" 
                  className="drum-button-outline"
                >
                  Explore Services
                </Link>
                <Link 
                  to="/contact" 
                  className="drum-button-secondary"
                >
                  Get in Touch
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials; 